/**
 * Portfolio Store
 * Manages portfolio state, holdings, and calculations using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  Portfolio, 
  PortfolioHolding, 
  PortfolioSummary, 
  AssetAllocation, 
  PerformanceMetrics,
  Transaction 
} from '../types/portfolio';
import { Asset as ApiAsset, AssetType } from '../types/api';
import { Asset } from './assetStore';
import { getAssetCurrency } from '../utils/assetCurrency';
import { currencyConverter } from '../services/currencyConverter';

// Helper function to prefetch exchange rates for holdings
const prefetchExchangeRates = async (holdings: PortfolioHolding[], baseCurrency: string): Promise<void> => {
  const currenciesNeeded = new Set<string>();
  
  holdings.forEach(holding => {
    const assetCurrency = getAssetCurrency(holding.asset);
    if (assetCurrency !== baseCurrency) {
      currenciesNeeded.add(`${assetCurrency}_${baseCurrency}`);
    }
  });

  // Prefetch all needed rates
  await Promise.allSettled(
    Array.from(currenciesNeeded).map(async (pair) => {
      const [from, to] = pair.split('_');
      try {
        await currencyConverter.getExchangeRate(from, to);
      } catch (error) {
        // Silently fail - we'll use cached or manual rates
        console.warn(`Failed to prefetch rate ${from}/${to}:`, error);
      }
    })
  );
};

interface PortfolioState {
  // State
  portfolio: Portfolio | null;
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;

  // Actions
  initializePortfolio: (name: string, description?: string) => void;
  addHolding: (asset: Asset, quantity: number, averageCost: number) => void;
  updateHolding: (holdingId: string, updates: Partial<PortfolioHolding>) => void;
  removeHolding: (holdingId: string) => void;
  addTransaction: (transaction: Omit<Transaction, 'id' | 'timestamp'>) => void;
  updatePortfolioFromPrices: (assetPrices: Record<string, number>) => Promise<void>;
  syncHoldingsFromAssets: (assets: Asset[]) => void;
  calculateSummary: () => void;
  calculateAllocation: () => void;
  calculatePerformance: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearPortfolio: () => void;
}

// Helper function to convert price to base currency
const convertToBaseCurrency = (price: number, assetCurrency: string, baseCurrency: string): number => {
  if (assetCurrency === baseCurrency || price === 0) {
    return price;
  }

  // Try to get cached rate first (synchronous)
  const rate = currencyConverter.getCachedRate(assetCurrency, baseCurrency);
  if (rate !== null) {
    return price * rate;
  }

  // If no cached rate, return original price (will be updated when rate is fetched)
  // This ensures the UI doesn't break while waiting for exchange rates
  return price;
};

// Helper functions for calculations
const calculateHoldingValue = (
  holding: PortfolioHolding, 
  currentPrice?: number,
  baseCurrency: string = 'USD'
): PortfolioHolding => {
  const assetCurrency = getAssetCurrency(holding.asset);
  const price = currentPrice || holding.asset.price || holding.averageCost;
  
  // Convert prices to base currency
  const priceInBaseCurrency = convertToBaseCurrency(price, assetCurrency, baseCurrency);
  const averageCostInBaseCurrency = convertToBaseCurrency(holding.averageCost, assetCurrency, baseCurrency);
  
  const currentValue = holding.quantity * priceInBaseCurrency;
  const totalCost = holding.quantity * averageCostInBaseCurrency;
  const unrealizedPnL = currentValue - totalCost;
  const unrealizedPnLPercent = totalCost > 0 ? (unrealizedPnL / totalCost) * 100 : 0;

  return {
    ...holding,
    currentValue,
    totalCost,
    unrealizedPnL,
    unrealizedPnLPercent,
    lastUpdated: new Date(),
  };
};

const calculatePortfolioSummary = (
  holdings: PortfolioHolding[],
  baseCurrency: string = 'USD'
): PortfolioSummary => {
  const totalValue = holdings.reduce((sum, holding) => sum + holding.currentValue, 0);
  const totalCost = holdings.reduce((sum, holding) => sum + holding.totalCost, 0);
  const totalPnL = totalValue - totalCost;
  const totalPnLPercent = totalCost > 0 ? (totalPnL / totalCost) * 100 : 0;

  // Calculate day change (simplified - would need historical data for accurate calculation)
  // Day change also needs to be converted to base currency
  const dayChange = holdings.reduce((sum, holding) => {
    if (!holding.asset.change24h) return sum;
    const assetCurrency = getAssetCurrency(holding.asset);
    const changeInBaseCurrency = convertToBaseCurrency(
      holding.asset.change24h,
      assetCurrency,
      baseCurrency
    );
    const dayChangeValue = holding.quantity * changeInBaseCurrency;
    return sum + dayChangeValue;
  }, 0);
  const dayChangePercent = totalValue > 0 ? (dayChange / (totalValue - dayChange)) * 100 : 0;

  return {
    totalValue,
    totalCost,
    totalPnL,
    totalPnLPercent,
    dayChange,
    dayChangePercent,
    lastUpdated: new Date(),
  };
};

const calculateAssetAllocation = (holdings: PortfolioHolding[], totalValue: number): AssetAllocation[] => {
  const allocationMap = new Map<AssetType, { value: number; count: number }>();

  holdings.forEach(holding => {
    const type = holding.asset.type;
    const existing = allocationMap.get(type) || { value: 0, count: 0 };
    allocationMap.set(type, {
      value: existing.value + holding.currentValue,
      count: existing.count + 1,
    });
  });

  return Array.from(allocationMap.entries()).map(([type, data]) => ({
    type,
    value: data.value,
    percentage: totalValue > 0 ? (data.value / totalValue) * 100 : 0,
    count: data.count,
  }));
};

const calculatePerformanceMetrics = (holdings: PortfolioHolding[], summary: PortfolioSummary): PerformanceMetrics => {
  const totalReturn = summary.totalPnL;
  const totalReturnPercent = summary.totalPnLPercent;
  
  // Day metrics from summary
  const dayReturn = summary.dayChange;
  const dayReturnPercent = summary.dayChangePercent;

  // Find best and worst performers
  let bestPerformer: PortfolioHolding | null = null;
  let worstPerformer: PortfolioHolding | null = null;

  holdings.forEach(holding => {
    if (!bestPerformer || holding.unrealizedPnLPercent > bestPerformer.unrealizedPnLPercent) {
      bestPerformer = holding;
    }
    if (!worstPerformer || holding.unrealizedPnLPercent < worstPerformer.unrealizedPnLPercent) {
      worstPerformer = holding;
    }
  });

  return {
    totalReturn,
    totalReturnPercent,
    dayReturn,
    dayReturnPercent,
    weekReturn: 0, // Would need historical data
    weekReturnPercent: 0,
    monthReturn: 0, // Would need historical data
    monthReturnPercent: 0,
    yearReturn: 0, // Would need historical data
    yearReturnPercent: 0,
    bestPerformer,
    worstPerformer,
  };
};

export const usePortfolioStore = create<PortfolioState>()(
  persist(
    (set, get) => ({
      // Initial state
      portfolio: null,
      transactions: [],
      isLoading: false,
      error: null,

      // Actions
      initializePortfolio: (name: string, description?: string) => {
        const portfolio: Portfolio = {
          id: `portfolio-${Date.now()}`,
          name,
          description,
          holdings: [],
          summary: {
            totalValue: 0,
            totalCost: 0,
            totalPnL: 0,
            totalPnLPercent: 0,
            dayChange: 0,
            dayChangePercent: 0,
            lastUpdated: new Date(),
          },
          allocation: [],
          performance: {
            totalReturn: 0,
            totalReturnPercent: 0,
            dayReturn: 0,
            dayReturnPercent: 0,
            weekReturn: 0,
            weekReturnPercent: 0,
            monthReturn: 0,
            monthReturnPercent: 0,
            yearReturn: 0,
            yearReturnPercent: 0,
            bestPerformer: null,
            worstPerformer: null,
          },
          config: {
            baseCurrency: 'USD',
            displayPrecision: 2,
            autoRefresh: true,
            refreshInterval: 30,
            showUnrealizedPnL: true,
            showPercentages: true,
          },
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        set({ portfolio, error: null });
      },

      addHolding: (asset: Asset, quantity: number, averageCost: number) => {
        const state = get();
        if (!state.portfolio) return;

        const holding: PortfolioHolding = {
          id: `holding-${Date.now()}`,
          asset: asset as any,
          quantity,
          averageCost,
          currentValue: quantity * (asset.price || averageCost),
          totalCost: quantity * averageCost,
          unrealizedPnL: 0,
          unrealizedPnLPercent: 0,
          weight: 0,
          lastUpdated: new Date(),
        };

        const baseCurrency = state.portfolio.config.baseCurrency;
        const updatedHolding = calculateHoldingValue(holding, asset.price, baseCurrency);
        const updatedHoldings = [...state.portfolio.holdings, updatedHolding];
        const summary = calculatePortfolioSummary(updatedHoldings, baseCurrency);
        const allocation = calculateAssetAllocation(updatedHoldings, summary.totalValue);
        const performance = calculatePerformanceMetrics(updatedHoldings, summary);

        // Update weights
        const holdingsWithWeights = updatedHoldings.map(h => ({
          ...h,
          weight: summary.totalValue > 0 ? (h.currentValue / summary.totalValue) * 100 : 0,
        }));

        set({
          portfolio: {
            ...state.portfolio,
            holdings: holdingsWithWeights,
            summary,
            allocation,
            performance,
            updatedAt: new Date(),
          },
        });
      },

      updateHolding: (holdingId: string, updates: Partial<PortfolioHolding>) => {
        const state = get();
        if (!state.portfolio) return;

        const baseCurrency = state.portfolio.config.baseCurrency;
        const updatedHoldings = state.portfolio.holdings.map(holding =>
          holding.id === holdingId ? { ...holding, ...updates } : holding
        );

        const summary = calculatePortfolioSummary(updatedHoldings, baseCurrency);
        const allocation = calculateAssetAllocation(updatedHoldings, summary.totalValue);
        const performance = calculatePerformanceMetrics(updatedHoldings, summary);

        set({
          portfolio: {
            ...state.portfolio,
            holdings: updatedHoldings,
            summary,
            allocation,
            performance,
            updatedAt: new Date(),
          },
        });
      },

      removeHolding: (holdingId: string) => {
        const state = get();
        if (!state.portfolio) return;

        const baseCurrency = state.portfolio.config.baseCurrency;
        const updatedHoldings = state.portfolio.holdings.filter(h => h.id !== holdingId);
        const summary = calculatePortfolioSummary(updatedHoldings, baseCurrency);
        const allocation = calculateAssetAllocation(updatedHoldings, summary.totalValue);
        const performance = calculatePerformanceMetrics(updatedHoldings, summary);

        set({
          portfolio: {
            ...state.portfolio,
            holdings: updatedHoldings,
            summary,
            allocation,
            performance,
            updatedAt: new Date(),
          },
        });
      },

      addTransaction: (transaction: Omit<Transaction, 'id' | 'timestamp'>) => {
        const state = get();
        const newTransaction: Transaction = {
          ...transaction,
          id: `tx-${Date.now()}`,
          timestamp: new Date(),
        };

        set({
          transactions: [...state.transactions, newTransaction],
        });
      },

      updatePortfolioFromPrices: async (assetPrices: Record<string, number>) => {
        const state = get();
        if (!state.portfolio) return;

        // Check if any prices have actually changed
        let hasChanges = false;
        const holdingsToUpdate = state.portfolio.holdings.map(holding => {
          const currentPrice = assetPrices[holding.asset.id];
          if (currentPrice !== undefined && currentPrice !== holding.asset.price) {
            hasChanges = true;
          }
          
          // 如果有新价格，同时更新 holding.asset.price
          return currentPrice !== undefined 
            ? { 
                ...holding, 
                asset: { ...holding.asset, price: currentPrice } 
              }
            : holding;
        });

        // Only update if prices have changed
        if (!hasChanges) return;

        // Prefetch exchange rates before calculating values
        const baseCurrency = state.portfolio.config.baseCurrency;
        await prefetchExchangeRates(holdingsToUpdate, baseCurrency);

        // Now calculate values with rates available
        const updatedHoldings = holdingsToUpdate.map(holding => {
          const currentPrice = assetPrices[holding.asset.id];
          return calculateHoldingValue(holding, currentPrice || holding.asset.price, baseCurrency);
        });

        const summary = calculatePortfolioSummary(updatedHoldings, baseCurrency);
        const allocation = calculateAssetAllocation(updatedHoldings, summary.totalValue);
        const performance = calculatePerformanceMetrics(updatedHoldings, summary);

        // Update weights
        const holdingsWithWeights = updatedHoldings.map(h => ({
          ...h,
          weight: summary.totalValue > 0 ? (h.currentValue / summary.totalValue) * 100 : 0,
        }));

        set({
          portfolio: {
            ...state.portfolio,
            holdings: holdingsWithWeights,
            summary,
            allocation,
            performance,
            updatedAt: new Date(),
          },
        });
      },

      calculateSummary: () => {
        const state = get();
        if (!state.portfolio) return;

        const baseCurrency = state.portfolio.config.baseCurrency;
        const summary = calculatePortfolioSummary(state.portfolio.holdings, baseCurrency);
        set({
          portfolio: {
            ...state.portfolio,
            summary,
            updatedAt: new Date(),
          },
        });
      },

      calculateAllocation: () => {
        const state = get();
        if (!state.portfolio) return;

        const allocation = calculateAssetAllocation(state.portfolio.holdings, state.portfolio.summary.totalValue);
        set({
          portfolio: {
            ...state.portfolio,
            allocation,
            updatedAt: new Date(),
          },
        });
      },

      calculatePerformance: () => {
        const state = get();
        if (!state.portfolio) return;

        const performance = calculatePerformanceMetrics(state.portfolio.holdings, state.portfolio.summary);
        set({
          portfolio: {
            ...state.portfolio,
            performance,
            updatedAt: new Date(),
          },
        });
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
      clearPortfolio: () => set({ portfolio: null, transactions: [], error: null }),

      // Sync holdings from asset store
      syncHoldingsFromAssets: (assets: Asset[]) => {
        const state = get();
        if (!state.portfolio) return;

        // Get assets with holdings > 0
        const assetsWithHoldings = assets.filter(asset => asset.holdings && asset.holdings > 0);
        
        // Check if we need to update at all
        const assetHoldingsMap = new Map(assets.map(a => [a.id, a.holdings || 0]));
        
        // Check if holdings have actually changed
        let hasChanges = false;
        
        // Check if any existing holdings have different quantities
        for (const holding of state.portfolio.holdings) {
          const assetHoldings = assetHoldingsMap.get(holding.asset.id) || 0;
          if (assetHoldings !== holding.quantity) {
            hasChanges = true;
            break;
          }
        }
        
        // Check if there are new assets with holdings
        if (!hasChanges) {
          const existingAssetIds = new Set(state.portfolio.holdings.map(h => h.asset.id));
          for (const asset of assetsWithHoldings) {
            if (!existingAssetIds.has(asset.id)) {
              hasChanges = true;
              break;
            }
          }
        }
        
        // If no changes, don't update
        if (!hasChanges && assetsWithHoldings.length === state.portfolio.holdings.length) return;

        // Create or update holdings
        const updatedHoldings = [...state.portfolio.holdings];

        assetsWithHoldings.forEach(asset => {
          const existingHoldingIndex = updatedHoldings.findIndex(h => h.asset.id === asset.id);
          
          if (existingHoldingIndex >= 0) {
            // Update existing holding quantity
            const baseCurrency = state.portfolio.config.baseCurrency;
            const holding = updatedHoldings[existingHoldingIndex];
            updatedHoldings[existingHoldingIndex] = calculateHoldingValue(
              { ...holding, quantity: asset.holdings! },
              asset.price,
              baseCurrency
            );
          } else {
            // Create new holding using current price as average cost
            const averageCost = asset.price || 0;
            const holding: PortfolioHolding = {
              id: `holding-${asset.id}-${Date.now()}`,
              asset: asset as any,
              quantity: asset.holdings!,
              averageCost,
              currentValue: asset.holdings! * (asset.price || averageCost),
              totalCost: asset.holdings! * averageCost,
              unrealizedPnL: 0,
              unrealizedPnLPercent: 0,
              weight: 0,
              lastUpdated: new Date(),
            };
            const baseCurrency = state.portfolio.config.baseCurrency;
            updatedHoldings.push(calculateHoldingValue(holding, asset.price, baseCurrency));
          }
        });

        // Remove holdings for assets with 0 holdings
        const baseCurrency = state.portfolio.config.baseCurrency;
        const filteredHoldings = updatedHoldings.filter(h => {
          const holdings = assetHoldingsMap.get(h.asset.id);
          return holdings !== undefined && holdings > 0;
        });

        const summary = calculatePortfolioSummary(filteredHoldings, baseCurrency);
        const allocation = calculateAssetAllocation(filteredHoldings, summary.totalValue);
        const performance = calculatePerformanceMetrics(filteredHoldings, summary);

        // Update weights
        const holdingsWithWeights = filteredHoldings.map(h => ({
          ...h,
          weight: summary.totalValue > 0 ? (h.currentValue / summary.totalValue) * 100 : 0,
        }));

        set({
          portfolio: {
            ...state.portfolio,
            holdings: holdingsWithWeights,
            summary,
            allocation,
            performance,
            updatedAt: new Date(),
          },
        });
      },
    }),
    {
      name: 'portfolio-storage',
      partialize: (state) => ({
        portfolio: state.portfolio,
        transactions: state.transactions,
      }),
    }
  )
);